import React, { FC, useEffect, useState, ChangeEvent, Fragment } from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
    Alert,
    Button,
    SelectChangeEvent,
} from '@mui/material';
import { CustomerType, LabClient } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import { InvoiceDelivery, InvoicingType, InvoicePeriod } from 'models/invoice';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import EmailSettingsForm from './email-settings-form/email-settings-form.component';
import ClinicInvoiceContent from './clinic-invoice-content/clinic-invoice-content.component';
import InvoiceContentForm from './invoice-content-form/invoice-content-form.component';
import { ClientDirInvoiceContentFormData, ClientDirInvoiceEmailSettingsFormData } from 'models/forms';
import { emptyInvoiceEmailSettingsData } from './email-settings-form/form-helper';
import { Organization } from 'models/organization';
import { useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import { labEInvoicingTemplate } from 'apollo/templates/e-invoicing';
import { CustomerEInvoicingStatus, LabEInvoicing } from 'models/e-invoicing';
import CustomerEInvoicingPanel from './customer-e-invoicing-panel/customer-e-invoicing-panel.component';

type InvoiceContentProps = {
    labClient: LabClient;
    currentLab: Organization;
    invoiceEnabled: boolean;
    setAllInvoiceSettings?: (settings: any) => void;
    onValidityChange?: (isValid: boolean) => void;
};
const InvoiceContent: FC<InvoiceContentProps> = ({
    labClient,
    currentLab,
    setAllInvoiceSettings,
    onValidityChange,
}: InvoiceContentProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const isLocalClient = labClient?.customer?.customerType === CustomerType.MY_CLIENT;
    const [overwriteMode, setOverwriteMode] = useState<boolean>(false);
    const [openConfirmDialog, setConfirmDialog] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState(InvoiceDelivery.PRINT);
    const [invoicingType, setInvoicingType] = useState(InvoicingType.COLLECTIVE);
    const [clinicForcedInvoicingType, setClinicForcedInvoiceType] = useState<InvoicingType>();
    const [clinicForcedInvoiceDelivery, setClinicForcedInvoicingDelivery] = useState<InvoiceDelivery>();
    const [modifiedLabClient, setModifiedLabClient] = useState<LabClient>(labClient);
    const [labOverrideClinicForcedSetting, setLabOverrideClinicForcedSetting] = useState<boolean>(false);
    const [invoicePeriod, setInvoicePeriod] = useState(InvoicePeriod.MONTHLY);
    const [isEmailSettingsValid, setIsEmailSettingsValid] = useState<boolean>(true);
    const [isInvoiceContentValid, setIsInvoiceContentValid] = useState<boolean>(true);
    // DD-3726: e-invoices need the lab to have enabled electronic invoicing.
    const { data: eInvoicingData } = useQuery<{ labEInvoicing: LabEInvoicing }>(labEInvoicingTemplate);
    const isEInvoicingDisabled = eInvoicingData?.labEInvoicing.enabled === false;
    const isEInvoicingEnabled = eInvoicingData?.labEInvoicing.enabled === true;
    // E-invoicing is only open for customers in the supported countries (the backend refuses
    // the rest); a customer already on e-invoice keeps it until changed.
    const customerCountry = (labClient?.customer?.contact?.address?.country || '').toUpperCase();
    const isCustomerCountrySupported = !!eInvoicingData?.labEInvoicing.supportedCountries?.includes(customerCountry);
    const savedDelivery = labClient?.invoiceSettings?.invoiceDelivery as InvoiceDelivery | undefined;
    const isSavedAsEInvoice =
        savedDelivery === InvoiceDelivery.ELECTRONIC_BILLING || savedDelivery === InvoiceDelivery.SCHEDULED_ELECTRONIC;
    const isElectronicDelivery =
        deliveryMethod === InvoiceDelivery.ELECTRONIC_BILLING ||
        deliveryMethod === InvoiceDelivery.SCHEDULED_ELECTRONIC;
    // Choosing e-invoice asks Crediflow whether the customer accepts it; until it answers the
    // choice cannot be saved, and if the customer has not accepted the previous method comes back.
    const [eInvoiceCheckRequest, setEInvoiceCheckRequest] = useState(0);
    const [deliveryBeforeEInvoice, setDeliveryBeforeEInvoice] = useState<InvoiceDelivery>();
    const [isCheckingEInvoice, setIsCheckingEInvoice] = useState(false);
    const handleEInvoiceChecked = (status: CustomerEInvoicingStatus) => {
        if (status !== CustomerEInvoicingStatus.READY && deliveryBeforeEInvoice) {
            setDeliveryMethod(deliveryBeforeEInvoice);
        }
        setDeliveryBeforeEInvoice(undefined);
    };

    const currentOrg = currentOrgVar();

    useEffect(() => {
        setModifiedLabClient(labClient);
        const forcedInvoiceType = customerSettings.getCustomerSetting(
            labClient?.customer?.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.INVOICING_TYPE,
        );
        const forcedInvoiceDelivery = customerSettings.getCustomerSetting(
            labClient?.customer?.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.INVOICING_DELIVERY,
        );

        const allowLabOverride = customerSettings.getCustomerSetting(
            currentOrg.customerSettings || [],
            CONSTANTS.SETTINGS_NAMES.ALLOW_LAB_INVOICE_OVERRIDE,
        );
        const labOverrideAllowed = allowLabOverride && forcedInvoiceDelivery !== InvoiceDelivery.SCHEDULED_ELECTRONIC;
        setLabOverrideClinicForcedSetting(labOverrideAllowed);

        if (forcedInvoiceType) {
            setClinicForcedInvoiceType(forcedInvoiceType);
        }
        if (forcedInvoiceDelivery) {
            setClinicForcedInvoicingDelivery(forcedInvoiceDelivery);
        }

        const fallbackType =
            (labClient?.invoiceSettings?.type as InvoicingType) || currentLab.invoicing?.defaultSetting?.invoiceType;
        const fallbackDelivery =
            (labClient?.invoiceSettings?.invoiceDelivery as InvoiceDelivery) ||
            currentLab.invoicing?.defaultSetting?.invoiceDelivery;

        const selectedType = labOverrideAllowed ? fallbackType : forcedInvoiceType || fallbackType;
        const selectedDelivery = labOverrideAllowed ? fallbackDelivery : forcedInvoiceDelivery || fallbackDelivery;

        setInvoicingType(selectedType);
        setDeliveryMethod(selectedDelivery);
        setInvoicePeriod(
            labClient?.invoiceSettings?.invoicePeriod ||
                currentLab.invoicing?.defaultSetting?.invoicePeriod ||
                InvoicePeriod.MONTHLY,
        );

        if (selectedDelivery === InvoiceDelivery.EMAIL) {
            const emailSettings = emptyInvoiceEmailSettingsData(labClient, currentOrg);
            setModifiedLabClient((prev) => ({
                ...prev,
                invoiceSettings: {
                    ...prev.invoiceSettings,
                    emailSettings,
                },
            }));
        }
    }, [labClient]);

    useEffect(() => {
        setOverwriteMode(labClient?.invoiceSettings?.isOverwritten || isLocalClient ? true : false);
    }, [labClient?.invoiceSettings?.isOverwritten]);

    useEffect(() => {
        if (!setAllInvoiceSettings) return;
        setAllInvoiceSettings({
            content: {
                type: invoicingType,
                invoiceDelivery: deliveryMethod,
                emailSettings:
                    deliveryMethod === InvoiceDelivery.EMAIL
                        ? modifiedLabClient.invoiceSettings?.emailSettings
                        : undefined,
                invoicePeriod:
                    invoicingType === InvoicingType.COLLECTIVE || invoicingType === InvoicingType.COLLECTIVE_DENTIST
                        ? invoicePeriod
                        : undefined,
                invoiceAddress:
                    modifiedLabClient.invoiceSettings?.invoiceAddress || modifiedLabClient.customer?.contact?.address,
                supplierReference: modifiedLabClient.invoiceSettings?.supplierReference,
                isOverwritten: overwriteMode,
            },
        });
    }, [invoicingType, deliveryMethod, invoicePeriod, modifiedLabClient, overwriteMode]);

    useEffect(() => {
        if (!onValidityChange) return;
        const isValid = deliveryMethod === InvoiceDelivery.EMAIL ? isEmailSettingsValid : isInvoiceContentValid;
        onValidityChange(isValid && !isCheckingEInvoice);
    }, [deliveryMethod, isEmailSettingsValid, isInvoiceContentValid, isCheckingEInvoice]);

    const overwriteModeSwitch = (event: ChangeEvent<HTMLInputElement>) => {
        setOverwriteMode(event.target.checked);
        if (!event.target.checked) {
            setConfirmDialog(true);
        }
    };

    const handleClose = () => {
        setOverwriteMode((prev) => !prev);
        setConfirmDialog(false);
    };

    const handleOk = () => {
        setConfirmDialog(false);
    };

    const isFortnoxEnabled = () => {
        return customerSettings.getCustomerSetting(currentOrg.customerSettings, CONSTANTS.SETTINGS_NAMES.FORTNOX);
    };

    const handleInvoiceDeliveryChange = (event: SelectChangeEvent) => {
        const invoiceDelivery = InvoiceDelivery[event.target.value as keyof typeof InvoiceDelivery];
        if (
            invoiceDelivery === InvoiceDelivery.ELECTRONIC_BILLING &&
            deliveryMethod !== InvoiceDelivery.ELECTRONIC_BILLING &&
            !clinicForcedInvoiceDelivery
        ) {
            setDeliveryBeforeEInvoice(deliveryMethod);
            setEInvoiceCheckRequest((request) => request + 1);
        }
        setDeliveryMethod(invoiceDelivery);
        if (invoiceDelivery === InvoiceDelivery.EMAIL) {
            const emailSettings = emptyInvoiceEmailSettingsData(labClient, currentOrg);
            setModifiedLabClient({
                ...modifiedLabClient,
                invoiceSettings: { ...modifiedLabClient.invoiceSettings, emailSettings: emailSettings },
            });
        }
    };

    const handleEmailSettingsChange = (formData: ClientDirInvoiceEmailSettingsFormData, isFormValid: boolean) => {
        setIsEmailSettingsValid(isFormValid);
        setModifiedLabClient({
            ...modifiedLabClient,
            invoiceSettings: { ...modifiedLabClient.invoiceSettings, emailSettings: formData },
        });
    };

    const handleInvoiceContentChange = (formData: ClientDirInvoiceContentFormData, isFormValid: boolean) => {
        setIsInvoiceContentValid(isFormValid);
        setModifiedLabClient({
            ...modifiedLabClient,
            invoiceSettings: {
                ...modifiedLabClient.invoiceSettings,
                supplierReference: formData.supplierReference,
                invoiceAddress: formData.invoiceAddress!,
            },
        });
    };

    return (
        <Box mt={3}>
            {labClient ? (
                <Fragment>
                    {clinicForcedInvoicingType || clinicForcedInvoiceDelivery ? (
                        <Box mb={2}>
                            <Alert severity="info">{translate('client-directory:CLIENT_FORCED_SETTINGS')}</Alert>
                        </Box>
                    ) : undefined}
                    {isElectronicDelivery && isEInvoicingDisabled ? (
                        <Box mb={2}>
                            <Alert
                                severity="warning"
                                data-test="client-invoice.e-invoicing-not-enabled"
                                action={
                                    <Button component={RouterLink} to="/lab-settings/invoicing" size="small">
                                        {translate('client-directory:E_INVOICING_GO_TO_SETTINGS')}
                                    </Button>
                                }
                            >
                                {translate('client-directory:E_INVOICING_NOT_ENABLED_FOR_CUSTOMER')}
                            </Alert>
                        </Box>
                    ) : undefined}
                    <Box mb={3}>
                        <Grid container spacing={2}>
                            <Grid
                                item
                                sm={
                                    invoicingType === InvoicingType.COLLECTIVE ||
                                    invoicingType === InvoicingType.COLLECTIVE_DENTIST
                                        ? 4
                                        : 8
                                }
                                xs={12}
                            >
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>{translate('client-directory:TYPE_OF_INVOICE')}</InputLabel>
                                    <Select
                                        required
                                        id="invoicing-type"
                                        disabled={!labOverrideClinicForcedSetting ? !!clinicForcedInvoicingType : false}
                                        label={translate('client-directory:TYPE_OF_INVOICE')}
                                        value={invoicingType}
                                        onChange={(event) =>
                                            setInvoicingType(
                                                InvoicingType[event.target.value as keyof typeof InvoicingType],
                                            )
                                        }
                                    >
                                        <MenuItem value={InvoicingType.COLLECTIVE}>
                                            {translate('client-directory:COLLECTIVE')}
                                        </MenuItem>
                                        <MenuItem value={InvoicingType.COLLECTIVE_DENTIST}>
                                            {translate('client-directory:COLLECTIVE_DENTIST')}
                                        </MenuItem>
                                        <MenuItem value={InvoicingType.SINGLE}>
                                            {translate('client-directory:SINGLE')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item sm={4} xs={12}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>{translate('client-directory:INVOICE_DELIVERY')}</InputLabel>
                                    <Select
                                        required
                                        id="delivery-method"
                                        disabled={!labOverrideClinicForcedSetting ? !!clinicForcedInvoicingType : false}
                                        label={translate('client-directory:INVOICE_DELIVERY')}
                                        value={deliveryMethod}
                                        onChange={handleInvoiceDeliveryChange}
                                    >
                                        <MenuItem value={InvoiceDelivery.PRINT}>
                                            {translate('client-directory:PRINT')}
                                        </MenuItem>
                                        <MenuItem value={InvoiceDelivery.EMAIL}>
                                            {translate('client-directory:EMAIL')}
                                        </MenuItem>
                                        {clinicForcedInvoiceDelivery === InvoiceDelivery.ELECTRONIC_BILLING ||
                                        savedDelivery === InvoiceDelivery.ELECTRONIC_BILLING ||
                                        (isEInvoicingEnabled &&
                                            isCustomerCountrySupported &&
                                            !clinicForcedInvoiceDelivery) ? (
                                            <MenuItem value={InvoiceDelivery.ELECTRONIC_BILLING}>
                                                {translate('client-directory:ELECTRONIC_BILLING')}
                                            </MenuItem>
                                        ) : undefined}
                                        {clinicForcedInvoiceDelivery === InvoiceDelivery.SCHEDULED_ELECTRONIC && (
                                            <MenuItem value={InvoiceDelivery.SCHEDULED_ELECTRONIC}>
                                                {translate('client-directory:SCHEDULED_ELECTRONIC')}
                                            </MenuItem>
                                        )}
                                        {isFortnoxEnabled() ? (
                                            <MenuItem value={InvoiceDelivery.EXTERNAL_INVOICE_SYSTEM}>
                                                {translate('client-directory:EXTERNAL_INVOICE_SYSTEM')}
                                            </MenuItem>
                                        ) : undefined}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {invoicingType === InvoicingType.COLLECTIVE ||
                            invoicingType === InvoicingType.COLLECTIVE_DENTIST ? (
                                <Grid item sm={4} xs={12}>
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel>{translate('client-directory:PERIOD')}</InputLabel>
                                        <Select
                                            required
                                            id="delivery-method"
                                            disabled={!invoicingType}
                                            label={translate('client-directory:PERIOD')}
                                            value={invoicePeriod}
                                            onChange={(event) =>
                                                setInvoicePeriod(
                                                    InvoicePeriod[event.target.value as keyof typeof InvoicePeriod],
                                                )
                                            }
                                        >
                                            <MenuItem value={InvoicePeriod.MONTHLY}>
                                                {translate('client-directory:MONTHLY')}
                                            </MenuItem>
                                            <MenuItem value={InvoicePeriod.SEMI_WEEKLY}>
                                                {translate('client-directory:SEMI_WEEKLY')}
                                            </MenuItem>
                                            <MenuItem value={InvoicePeriod.WEEKLY}>
                                                {translate('client-directory:WEEKLY')}
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            ) : undefined}
                        </Grid>
                    </Box>

                    {isEInvoicingEnabled && !isCustomerCountrySupported && !isSavedAsEInvoice ? (
                        <Box mb={2}>
                            <Alert severity="info" data-test="client-invoice.e-invoicing-country-not-supported">
                                {translate('client-directory:E_INVOICING_NOT_AVAILABLE_FOR_CUSTOMER_COUNTRY')}
                            </Alert>
                        </Box>
                    ) : undefined}
                    {isEInvoicingEnabled && (isCustomerCountrySupported || isSavedAsEInvoice) ? (
                        <CustomerEInvoicingPanel
                            clientId={labClient.customerId}
                            isElectronicDelivery={isElectronicDelivery}
                            checkRequest={eInvoiceCheckRequest}
                            canInvite={!!eInvoicingData?.labEInvoicing.canEnable}
                            defaultEmail={
                                labClient.invoiceSettings?.contactInformation?.email ||
                                labClient.customer?.contact?.communication?.email ||
                                ''
                            }
                            onChecked={handleEInvoiceChecked}
                            onCheckingChange={setIsCheckingEInvoice}
                        />
                    ) : undefined}

                    <Box mb={3}>
                        <Typography variant="h4" sx={{ marginBottom: 2 }}>
                            {translate('client-directory:INVOICE_CONTENT')}
                        </Typography>

                        {!clinicForcedInvoicingType &&
                        !clinicForcedInvoiceDelivery &&
                        labClient?.customer?.customerType !== CustomerType.MY_CLIENT ? (
                            <Box mb={2}>
                                <Box mb={1}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="secondary"
                                                checked={overwriteMode}
                                                onChange={overwriteModeSwitch}
                                            />
                                        }
                                        label={translate('client-directory:OVERWRITE_INVOICE_CONTENT')}
                                    />
                                </Box>
                                {!overwriteMode ? (
                                    <Alert severity="info">
                                        {translate('client-directory:OVERWRITE_INVOICE_CONTENT_DESC')}
                                    </Alert>
                                ) : undefined}
                            </Box>
                        ) : undefined}

                        {labClient?.customer?.customerType === CustomerType.MY_CLIENT || overwriteMode ? (
                            <InvoiceContentForm
                                labClient={labClient}
                                clinicForcedInvoiceDelivery={clinicForcedInvoiceDelivery}
                                clinicForcedInvoicingType={clinicForcedInvoicingType}
                                onChange={handleInvoiceContentChange}
                            />
                        ) : (
                            <ClinicInvoiceContent clinic={labClient.customer} />
                        )}
                    </Box>

                    {deliveryMethod === InvoiceDelivery.EMAIL && labClient ? (
                        <Box mb={3}>
                            <EmailSettingsForm
                                labClient={labClient}
                                onChange={handleEmailSettingsChange}
                                clinicForcedInvoicingType={clinicForcedInvoicingType}
                                clinicForcedInvoiceDelivery={clinicForcedInvoiceDelivery}
                            />
                        </Box>
                    ) : undefined}

                    <ConfirmationDialog
                        open={openConfirmDialog}
                        handleClose={handleClose}
                        handleOk={handleOk}
                        title={translate('client-directory:CONFIRM_CHANGES')}
                        confirm={translate('client-directory:DIALOG_CONFIRM_INFO')}
                        process={translate('client-directory:CONFIRM_CHANGES')}
                    />
                </Fragment>
            ) : undefined}
        </Box>
    );
};

export default InvoiceContent;
